<template>
  <div
    class="image-gallery"
    :class="{
      'image-gallery--aspect-ratio': !!aspectRatio,
      'image-gallery--no-main': noMainImage,
      'image-gallery--upload': uploadScreenshot,
    }">
    <div class="image-gallery__top">
      <div class="image-gallery__info">
        <h3 class="image-gallery__info-title">
          Screenshots
        </h3>

        <span class="image-gallery__info-count">
          {{ localImages.length }}
        </span>
      </div>

      <div class="image-gallery__controls">
        <img
          src="/images/icons/arrow-simple.svg"
          class="image-gallery__controls-arrow"
          alt="Prev slide"
          width="24"
          height="24"
          @click="prevSlide">

        <img
          src="/images/icons/arrow-simple.svg"
          class="image-gallery__controls-arrow"
          alt="Next slide"
          width="24"
          height="24"
          @click="nextSlide">
      </div>
    </div>

    <template v-if="uploadScreenshot">
      <ScreenshotsGalleryLinksUploader
        class="image-gallery__links-uploader"
        @screenshots-uploaded="addScreenshots" />

      <FileUploader
        class="image-gallery__main glide"
        data-glide-el="track"
        upload-screenshot
        multiple
        @screenshot-uploaded="addScreenshot" />
    </template>

    <div
      v-else
      class="image-gallery__main glide"
      :class="{ 'image-gallery__main--hidden': noMainImage }">
      <div
        class="glide__track"
        data-glide-el="track">
        <div class="glide__slides">
          <img
            v-for="(image, index) in localImages"
            :key="index"
            :src="image"
            class="glide__slide image-gallery__main-image"
            :style="aspectRatio ? `aspect-ratio: ${aspectRatio}` : ''"
            @click="handleImageClick(index)">
        </div>
      </div>
    </div>

    <div
      ref="thumbnailList"
      :class="['image-gallery__thumbnails', { '_scrollbar-hidden': !uploadScreenshot }]">
      <component
        :is="uploadScreenshot ? 'draggable' : 'TransparentWrapper'"
        v-model="localImages"
        class="image-gallery__draggable-wrapper"
        @end="moveScreenshot">
        <div
          v-for="(thumbnail, index) in localImages"
          :key="thumbnail"
          class="image-gallery__thumbnail-wrapper"
          :class="{ 'image-gallery__thumbnail-wrapper--no-main': noMainImage }"
          @touchstart="handleThumbTouchStart"
          @touchmove="handleThumbTouchMove"
          @click="handleThumbnailClick(index)">
          <img
            :src="thumbnail"
            :class="['image-gallery__thumbnail']">

          <div
            v-if="uploadScreenshot"
            class="image-gallery__thumbnail-overlay">
            <img
              src="/images/icons/bin.svg"
              class="image-gallery__thumbnail-icon"
              @click="deleteScreenshot(index)">
          </div>
        </div>
      </component>
    </div>

    <FsLightbox
      v-if="activateLightbox && isMounted"
      :toggler="lightboxToggler"
      :slide="lightboxSlide"
      :sources="localImages"
      :exit-fullscreen-on-close="true"
      @wheel.native="zoomImage($event)" />
  </div>
</template>

<script>
import Glide from '@glidejs/glide';
import FsLightbox from 'fslightbox-vue';
import { mapState } from 'vuex';
import { wheelHandler } from 'Root/helpers/wheelHandler';
import FileUploader from 'Root/pages/booster/components/FileUploader.vue';
import ScreenshotsGalleryLinksUploader from 'Components/Accounts/ScreenshotsGalleryLinksUploader.vue';
import TransparentWrapper from 'Components/TransparentWrapper.vue';
import FsLightBoxZoomMixin from 'Root/mixins/fsLightBoxZoomMixin';
import draggable from 'vuedraggable';

export default {
  components: {
    ScreenshotsGalleryLinksUploader,
    FileUploader,
    FsLightbox,
    draggable,
    TransparentWrapper,
  },

  mixins: [FsLightBoxZoomMixin],

  props: {
    images: {
      type: Array,
      default: () => [],
    },

    uploadScreenshot: {
      type: Boolean,
      default: false,
    },

    noMainImage: {
      type: Boolean,
      default: false,
    },

    activateLightbox: {
      type: Boolean,
      default: true,
    },

    aspectRatio: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      activeIndex: 0,
      glide: null,
      wasInitGlide: false,
      lightboxToggler: false,
      lightboxSlide: 0,
      localImages: [],
      isMounted: false,
    };
  },

  computed: {
    ...mapState(['userDevice']),
  },

  watch: {
    images(newImages) {
      this.localImages = [...newImages];

      this.$nextTick(() => {
        if (!this.uploadScreenshot && this.localImages.length) {
          if (this.glide) {
            this.glide.destroy();
          }
          this.initGlide();
        }
      });
    },
  },

  mounted() {
    this.isMounted = true;

    if (!this.uploadScreenshot && this.images.length > 1) {
      this.$nextTick(() => {
        this.initGlide();
      });
    }

    this.initLocalImages();

    if (!this.userDevice.hasTouch) {
      this.$refs.thumbnailList.addEventListener('wheel', wheelHandler);
      document.addEventListener('keydown', this.keydownHandler);
    }
  },

  beforeDestroy() {
    this.destroyGlide();

    if (!this.userDevice.hasTouch) {
      this.$refs.thumbnailList.removeEventListener('wheel', wheelHandler);
      document.removeEventListener('keydown', this.keydownHandler);
    }
  },

  methods: {
    deleteScreenshot(index) {
      this.localImages.splice(index, 1);
      this.$emit('screenshot-deleted', index);
    },

    initGlide() {
      this.glide = new Glide('.glide', {
        type: 'carousel',
        perView: 1,
        gap: 10,
      });

      this.glide.mount();

      this.glide.on('run.after', () => {
        this.activeIndex = this.glide.index;
        this.scrollToActiveThumbnail();
      });

      this.wasInitGlide = true;
    },

    destroyGlide() {
      if (this.glide) {
        this.glide.destroy();
        this.glide = null;
      }
      this.wasInitGlide = false;
    },

    setActiveImage(index) {
      if (this.glide) {
        this.glide.go(`=${index}`);
        this.activeIndex = index;
        this.scrollToActiveThumbnail();
      }
    },

    prevSlide() {
      if (this.uploadScreenshot) {
        this.activeIndex = (this.activeIndex - 1 + this.localImages.length) % this.localImages.length;
        this.scrollToActiveThumbnail();
      } else if (this.glide) {
        this.glide.go('<');
      }
    },

    nextSlide() {
      if (this.uploadScreenshot) {
        this.activeIndex = (this.activeIndex + 1) % this.localImages.length;
        this.scrollToActiveThumbnail();
      } else if (this.glide) {
        this.glide.go('>');
      }
    },

    handleThumbTouchStart(event) {
      this.thumbStartX = event.touches[0].clientX;
    },

    handleThumbTouchMove(event) {
      this.thumbEndX = event.touches[0].clientX;
      const delta = this.thumbStartX - this.thumbEndX;
      event.currentTarget.scrollLeft += delta;
      this.thumbStartX = this.thumbEndX;
    },

    handleImageClick(index) {
      this.openLightbox(index);
    },

    handleThumbnailClick(index) {
      if (this.uploadScreenshot) {
        return;
      }

      this.setActiveImage(index);
      this.openLightbox(index);
    },

    openLightbox(index) {
      this.setActiveImage(index);
      this.lightboxSlide = index + 1;
      this.lightboxToggler = !this.lightboxToggler;
    },

    scrollToActiveThumbnail() {
      const thumbnail = this.$refs.thumbnailList.children[this.activeIndex];
      thumbnail.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    },

    moveScreenshot() {
      this.$emit('screenshot-moved', this.localImages);
    },

    addScreenshot(screenshotPath) {
      this.localImages.unshift(screenshotPath);
      this.$emit('image-uploaded', screenshotPath);
    },

    addScreenshots(screenshots) {
      this.localImages = [...this.localImages, ...screenshots];
      this.$emit('images-uploaded', screenshots);
    },

    initLocalImages() {
      this.localImages = [...this.images];
    },
  },
};
</script>

<style lang="scss" scoped>
.image-gallery {
  display: flex;
  flex-direction: column;
  align-items: start;
  padding: 24px 24px 54px 24px;
  width: 100%;
  max-height: 524px;
  border: 1px solid rgba($color--basic-menu, 0.8);
  background-color: rgba($color--basic-black, 0.8);
  border-radius: 16px;
  min-width: 0;
}

.image-gallery--no-main {
  padding: 24px;
}

.image-gallery--upload {
  max-height: unset;
}

.image-gallery--aspect-ratio {
  max-height: none;

  .image-gallery__main {
    max-height: none;
  }

  .image-gallery__main-image {
    max-height: none;
  }
}

.image-gallery__main {
  position: relative;
  width: 100%;
  max-height: 300px;
  margin-bottom: 24px;
  border-radius: 16px;
  overflow: hidden;
}

.image-gallery__top {
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;
}

.image-gallery__info {
  display: flex;
  align-items: center;
}

.image-gallery__info-title {
  font-size: 19px;
  font-weight: 500;
  margin-right: 10px;
}

.image-gallery__info-count {
  font-size: 19px;
  color: $color--basic-superdark-gray;
}

.glide__track {
  width: 100%;
  overflow: hidden;
  position: relative;
}

.image-gallery__main-image {
  width: 100%;
  max-height: 300px;
  border-radius: 10px;
  flex-shrink: 0;
  cursor: pointer;
  object-fit: cover;
}

.image-gallery__controls {
  display: flex;
  justify-content: space-between;
}

.image-gallery__controls-arrow {
  cursor: pointer;

  &:first-of-type {
    margin-right: 16px;
    transform: rotate(180deg);
  }
}

.image-gallery__thumbnails {
  display: flex;
  overflow-x: auto;
  width: 100%;
  min-height: 90px;

  &._scrollbar-hidden {
    min-height: 0;
    scrollbar-width: none;
  }
}

.image-gallery__draggable-wrapper {
  display: contents;
  width: 100%;

  .image-gallery__thumbnail-wrapper {
    cursor: grab;

    &:first-child {
      margin-right: 20px;
      position: relative;

      &:after {
        content: '';
        position: absolute;
        right: -10px;
        width: 1px;
        height: 80%;
        transform: translateY(10%);
        background: $color--additional-gray;
      }
    }
  }
}

.image-gallery__thumbnail-wrapper {
  position: relative;
  cursor: pointer;
  width: 72px;
  height: 72px;
  border-radius: 12px;
  flex-shrink: 0;

  &:not(:last-child) {
    margin-right: 10px;
  }

}

.image-gallery__thumbnail-wrapper--no-main {
  &:not(:last-child) {
    margin-right: 16px;
  }
}

.image-gallery__thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 1;
  transition: transform 0.2s, opacity 0.2s;
  border-radius: 10px;
}

.image-gallery__thumbnail-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s;
  border-radius: 10px;
}

.image-gallery__thumbnail-icon {
  width: 24px;
  height: 24px;
  cursor: pointer;
  opacity: .5;
}
.image-gallery__thumbnail-icon:hover {
  opacity: 1;
}

.image-gallery__thumbnail-wrapper:hover .image-gallery__thumbnail-overlay {
  opacity: 1;
}

.image-gallery__thumbnail--active {
  opacity: 1;
}

.image-gallery__thumbnail:hover {
  transform: scale(1.05);
}

.image-gallery__thumbnails::-webkit-scrollbar {
  display: none;
}

.image-gallery__main--hidden {
  display: none;
}

.image-gallery__links-uploader {
  width: 100%;
  margin-bottom: 24px;
}

@media screen and #{$screen-less-than-640} {
  .image-gallery__main-image {
    transform: translateY(0px);
  }
}
</style>
